// src/pages/PurchaseManagement.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from "@mui/icons-material";
import { apiUrl } from "../config/api";

const PURCHASES_API_URL = apiUrl('purchase_items.php');

const fmtBool = (n) => (Number(n) ? "Yes" : "No");

export default function PurchaseManagement() {
  // data
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // filters
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("1"); // show active by default

  // dialog
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null); // row or null
  const [form, setForm] = useState({ category: "", name: "", active: 1 });
  const resetForm = () => setForm({ category: "", name: "", active: 1 });

  // categories for dropdown (from data)
  const categories = useMemo(() => {
    const s = new Set();
    items.forEach((x) => s.add(x.category));
    return Array.from(s).sort();
  }, [items]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (catFilter) params.append("category", catFilter);
      if (activeFilter !== "") params.append("active", activeFilter);
      params.append("limit", "1000");
      const r = await fetch(`${PURCHASES_API_URL}?${params.toString()}`);
      const d = await r.json();
      setItems(Array.isArray(d.items) ? d.items : []);
    } catch (e) {
      console.error(e);
      alert("Failed to load purchase items.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []); // initial

  const openAdd = () => {
    setEditing(null);
    resetForm();
    setDlgOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      category: row.category || "",
      name: row.name || "",
      active: Number(row.active ?? 1),
    });
    setDlgOpen(true);
  };
  const closeDlg = () => {
    setDlgOpen(false);
    setEditing(null);
  };

  const onSave = async () => {
    const payload = { ...form, category: (form.category || "").trim(), name: (form.name || "").trim() };
    if (!payload.category || !payload.name) {
      alert("Category and Name are required.");
      return;
    }
    try {
      if (editing) {
        // UPDATE
        const r = await fetch(PURCHASES_API_URL, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: editing.id, ...payload }),
        });
        const d = await r.json();
        if (d.status !== "ok") throw new Error(d.message || "Update failed");
        closeDlg();
        fetchItems();
      } else {
        // ADD
        const r = await fetch(PURCHASES_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const d = await r.json();
        if (d.status !== "ok") throw new Error(d.message || "Insert failed");
        closeDlg();
        fetchItems();
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete "${row.name}" from "${row.category}"?`)) return;
    try {
      const r = await fetch(PURCHASES_API_URL, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: row.id }),
      });
      const d = await r.json();
      if (d.status !== "ok") throw new Error(d.message || "Delete failed");
      fetchItems();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>Purchase Management</Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Search"
            size="small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or category"
          />
          <TextField
            select
            label="Category"
            size="small"
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">All</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Active"
            size="small"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            sx={{ width: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Active</MenuItem>
            <MenuItem value="0">Inactive</MenuItem>
          </TextField>

          <Box flexGrow={1} />

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchItems}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
          >
            Add Item
          </Button>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Items</Typography>
          <Box flexGrow={1} />
          {loading && <CircularProgress size={18} />}
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {items.length === 0 ? (
          <Typography color="text.secondary">No items found.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.category}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={fmtBool(row.active)}
                      color={Number(row.active) ? "success" : "default"}
                      size="small"
                      variant={Number(row.active) ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <span>
                          <IconButton color="primary" onClick={() => openEdit(row)} size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton color="error" onClick={() => onDelete(row)} size="small">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dlgOpen} onClose={closeDlg} fullWidth>
        <DialogTitle>
          {editing ? "Edit Item" : "Add New Item"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Category"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
          />
          <TextField
            select
            label="Active"
            value={String(form.active)}
            onChange={(e) => setForm((f) => ({ ...f, active: Number(e.target.value) }))}
            sx={{ width: 160 }}
          >
            <MenuItem value="1">Yes</MenuItem>
            <MenuItem value="0">No</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={closeDlg}>Cancel</Button>
          <Button startIcon={<SaveIcon />} onClick={onSave} variant="contained">
            {editing ? "Save Changes" : "Add Item"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
